/**
 * Returns true if the object has the property, false if not. Coupled with a
 * type check here for convenience.
 */
export function hasProperty<
  TSubject,
  TKey extends string,
  TType extends PossibleTypes = "unknown"
>(
  obj: TSubject,
  key: TKey,
  type?: TType
): obj is TSubject & Record<TKey, TypeScriptTypeFromString<TType>> {
  // For library code, we need a convenience accessor here, since TypeScript
  // can't infer all of the things we're asserting manually in code here
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  const anyObj = obj as any;

  // avoid prototype bugs with myobj.hasOwnProperty: https://eslint.org/docs/latest/rules/no-prototype-builtins
  const hasProp = Object.prototype.hasOwnProperty.call(obj, key);

  if (type === "array") {
    return hasProp && Array.isArray(anyObj[key]);
  }

  if (!hasProp) {
    return false;
  }

  if (!type) {
    return true;
  }

  return typeof anyObj[key] === type;
}

export type PossibleTypes =
  | "string"
  | "number"
  | "boolean"
  | "object"
  | "function"
  | "symbol"
  | "array"
  | "unknown";

export type TypeScriptTypeFromString<T extends PossibleTypes> =
  T extends "string"
    ? string
    : T extends "number"
    ? number
    : T extends "boolean"
    ? boolean
    : T extends "object"
    ? Record<string, unknown>
    : T extends "function"
    ? (...args: unknown[]) => unknown
    : T extends "symbol"
    ? symbol
    : T extends "array"
    ? unknown[]
    : unknown;
